import { ICustomisationSettings } from "./customisation.interface";
import { PersonAttributes,
  AdditionalAttributes,
  AssetType,
  LoanPurpose,
  OpportunityAttributes,
  PropertyPurchaseAttributes,
  PropertyPurchaseFinancials,
  MenuAttributes,
  TextAttributes,
  ProductSearchResultAttributes,
  ProductType,
  ProductFeature,
  ProductFeeType,
  ProductDetails
} from '@shared/enums/customisation.enum';

export const customisation: ICustomisationSettings = {
  applicant: {
    personDetails: [
      PersonAttributes.Title,
      PersonAttributes.FirstName,
      PersonAttributes.MiddleName,
      PersonAttributes.LastName,
      PersonAttributes.DateOfBirth,
      PersonAttributes.RelationshipStatus
    ],
    furtherDetails: [
      AdditionalAttributes.RepayingStudentLoan,
      AdditionalAttributes.PermanentResident,
      AdditionalAttributes.AustralianCitizen
    ],
    shouldDisplayContactSearchModal: false
  },
  purposeType: {
    homeLoan: [
      LoanPurpose.Purchase,
      LoanPurpose.Refinance,
      LoanPurpose.TitleBuyout,
      LoanPurpose.OtherPersonal,
      LoanPurpose.Business
    ],
    commercialLoan: [
      LoanPurpose.Purchase,
      LoanPurpose.Refinance,
      LoanPurpose.TitleBuyout,
      LoanPurpose.OtherPersonal,
      LoanPurpose.Business
    ],
    smsfLoan: [
      LoanPurpose.Purchase,
      LoanPurpose.Refinance
    ],
    motorLoan: [
      LoanPurpose.Consumer,
      LoanPurpose.Commercial
    ],
    personalLoan: [LoanPurpose.UnsecuredPersonal],
    insuranceLoan: [LoanPurpose.Insurance],
    refixLoan: [LoanPurpose.Refix]
  },
  summary: {
    opportunityDetail: [
      OpportunityAttributes.Name,
      OpportunityAttributes.EstimatedAmount,
      OpportunityAttributes.CreatedDate,
      OpportunityAttributes.WorkflowStatus,
      OpportunityAttributes.ProviderReference,
    ],
    showSubmissionErrorMessage: true
  },
  purpose: {
    propertyPurchaseDetails: [
      PropertyPurchaseAttributes.PurchaseAmount,
      PropertyPurchaseAttributes.LoanAmount,
      PropertyPurchaseAttributes.PropertyType,
      PropertyPurchaseAttributes.Zoning,
      PropertyPurchaseAttributes.LandSize,
      PropertyPurchaseAttributes.TitleType,
      PropertyPurchaseAttributes.PurchaseStatus,
      PropertyPurchaseAttributes.Address,
      PropertyPurchaseAttributes.UseAfterSettlement,
      PropertyPurchaseAttributes.SettlementDate,
      PropertyPurchaseAttributes.FinanceDate,
      PropertyPurchaseAttributes.UseAsSecurity,
    ],
    propertyPurchaseFinancials: [
      PropertyPurchaseFinancials.GenuineSavings,
      PropertyPurchaseFinancials.DepositPaid,
      PropertyPurchaseFinancials.Gift,
      PropertyPurchaseFinancials.OngoingWeeklyRent,
      PropertyPurchaseFinancials.FirstHomeBuyer,
      PropertyPurchaseFinancials.FirstHomeBuyerGrant,
      PropertyPurchaseFinancials.StampDutyUpfront,
    ]
  },
  income: {
    paygIncomeDetails: [],
    excludePreviousYearByDefault: true
  },
  assets: {
    assetTypes: [
      AssetType.Property,
      AssetType.Vehicle,
      AssetType.Other,
      AssetType.PropertySecurityGuarantee
    ]
  },
  menu: {
    sideMenu: {
      menuAttributes: [],
      menuSections: [
        [
          {
            label: MenuAttributes.Summary,
            link: [],
            schemaPaths: []
          },
          {
            label: MenuAttributes.Applicants,
            link: ['Applicants'],
            schemaPaths: [
              'applicants/individuals',
              'applicants/companies',
              'applicants/trusts',
              'dependents'
            ]
          },
          {
            label: MenuAttributes.Income,
            link: ['Income'],
            schemaPaths: ['incomes']
          },
          {
            label: MenuAttributes.Assets,
            link: ['Assets'],
            schemaPaths: ['assets']
          },
          {
            label: MenuAttributes.Liabilities,
            link: ['Liabilities'],
            schemaPaths: ['liabilities', 'monthlyExpenses']
          },
          {
            label: MenuAttributes.Purpose,
            link: ['Purposes'],
            schemaPaths: ['purposes']
          }
        ],
        [
          {
            label: MenuAttributes.FundsPosition,
            link: ['FundsPosition']
          },
          {
            label: MenuAttributes.ProductSelection,
            link: ['ProductSelection']
          }
        ]
      ],
      showSubmitButton: true
    }
  },

  text: {
    [TextAttributes.OpportunityNameLabel]: 'Application name',
    [TextAttributes.CopyDetailsLabel]:
      'Copy details from an existing application',
    [TextAttributes.SearchOpportunitiesPlaceholder]:
      'search for an application',
    [TextAttributes.OpportunityHeader]: 'Application details',
    [TextAttributes.EditOpportunity]: 'Edit Application',
    [TextAttributes.OpportunityNameValidation]: 'Application name is required',
    [TextAttributes.OpportunitySelectionRequired]:
      'Please select an application',
    [TextAttributes.SubmissionModalHeaderSubmitting]: 'Submitting Application',
    [TextAttributes.SubmissionModalHeaderSubmitted]: 'Success!',
    [TextAttributes.SubmissionInProgress]:
      "Almost there! We're getting everything ready for you...",
    [TextAttributes.SubmissionComplete]:
      "<b>Your application has been submitted.</b>\nNext, download the application form and upload your supporting documents in Loan Tracker. We'll start the assessment as soon as we receive everything.",
    [TextAttributes.SubmissionModalButton]: 'Go to Loan Tracker',
    [TextAttributes.SubmissionStatusTimeOut]:
      "We're still processing your application. Don't worry - it's continuing in the background!\n\nPlease refresh the page in a few minutes to check its progress.",
    [TextAttributes.SubmissionProductRecommendationRequired]:
      'A product selection is required prior to submitting the application',
    [TextAttributes.ProductSearchPlaceholder]: 'search by product',
    [TextAttributes.SubmitOpportunityTitle]: 'Submit Application'
  },
  product: {
    searchResultDetails: [
      ProductSearchResultAttributes.ProductName,
      ProductSearchResultAttributes.Rate,
      ProductSearchResultAttributes.RateType,
      ProductSearchResultAttributes.Term,
      ProductSearchResultAttributes.MaxLVR,
      ProductSearchResultAttributes.MaxLoanAmount
    ],
    productType: ProductType.PackageOnly,
    excludedFeatures: [
      ProductFeature.ProPackDiscount,
      ProductFeature.LineOfCredit,
      ProductFeature.Construction,
      ProductFeature.FamilyGuarantee,
      ProductFeature.InterestCapitalise,
      ProductFeature.Bridging,
      ProductFeature.ReverseMortgage
    ],
    showProductSelectionMinimum: false,
    showRecommended: false,
    filterByProductGroup: true,
    disableProductsByMaxLoanAmount: true,
    skipAmountAndShowPackageSummary: true,
    PRODUCT_SELECTION_MAXIMUM: 1,
    limitSingleProductSelection: true,
    details: [
      ProductDetails.Product,
      ProductDetails.Amount,
      ProductDetails.LoanTerm
    ],
    readOnlyFees: [
      ProductFeeType.Application,
      ProductFeeType.Valuation,
      ProductFeeType.Discharge,
      ProductFeeType.MonthlyService,
      ProductFeeType.AnnualService
    ]
  },
  submission: {
    showSubmissionModal: true,
    emitSubmissionEvent: true
  }
};
